import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareText, Save, Loader2, ShieldAlert, Mail, Bell, Phone, Smartphone, Info, UserCheck } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", amber: "#B7791F", red: "#9C1E22", green: "#2F7D5A" };

const Row = ({ title, desc, children }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b last:border-0" style={{ borderColor: C.line }}>
    <div className="min-w-0"><div className="text-[13.5px] font-medium" style={{ color: C.charcoal }}>{title}</div>
      {desc && <div className="text-[12px]" style={{ color: C.slate }}>{desc}</div>}</div>
    <div className="shrink-0">{children}</div>
  </div>
);

const ProviderChip = ({ name, status }) => (
  <span className="text-[11px] px-2 py-0.5 rounded-full border" style={{ borderColor: C.line, color: status === "connected" ? C.green : C.slate, background: status === "connected" ? "rgba(47,125,90,0.08)" : "#F6F8FA" }}>
    {name}: {status === "connected" ? "Connected" : "Not connected yet"}
  </span>
);

export default function SettingsCommunications() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [prefs, setPrefs] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingP, setSavingP] = useState(false);
  const [savingS, setSavingS] = useState(false);
  const [avail, setAvail] = useState(null);
  const [staff, setStaff] = useState([]);
  const [savingA, setSavingA] = useState(false);

  useEffect(() => {
    api.get("/communications/preferences/me").then(({ data }) => setPrefs(data.preferences)).catch((e) => toast.error(errMsg(e)));
    if (isAdmin) api.get("/communications/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/communications/availability/me").then(({ data }) => setAvail(data.availability)).catch(() => {});
    api.get("/staff").then(({ data }) => setStaff((data.items || data || []).map((u) => ({ id: u.id, name: u.full_name || u.email })))).catch(() => {});
  }, [isAdmin]);

  const setP = (k, v) => setPrefs((x) => ({ ...x, [k]: v }));
  const setS = (k, v) => setSettings((x) => ({ ...x, [k]: v }));
  const setAv = (k, v) => setAvail((x) => ({ ...x, [k]: v }));

  const saveAvail = async () => {
    setSavingA(true);
    try {
      const { data } = await api.put("/communications/availability/me", {
        available_now: avail.available_now, out_of_office: avail.out_of_office,
        backup_user_id: avail.backup_user_id || null, working_hours: avail.working_hours || null,
        timezone: avail.timezone || null,
      });
      setAvail(data.availability); toast.success("Your availability was saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingA(false); }
  };

  const savePrefs = async () => {
    setSavingP(true);
    try {
      const { data } = await api.put("/communications/preferences/me", {
        in_app_enabled: prefs.in_app_enabled, email_enabled: prefs.email_enabled,
        phone_call_task_enabled: prefs.phone_call_task_enabled, marketing_email_opt_in: prefs.marketing_email_opt_in,
        preferred_contact_method: prefs.preferred_contact_method, quiet_hours_start: prefs.quiet_hours_start || null,
        quiet_hours_end: prefs.quiet_hours_end || null, timezone: prefs.timezone || null,
        unsubscribe_status: prefs.marketing_email_opt_in ? "subscribed" : prefs.unsubscribe_status,
      });
      setPrefs(data.preferences); toast.success("Your preferences were saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingP(false); }
  };
  const saveSettings = async () => {
    setSavingS(true);
    try {
      const { data } = await api.put("/communications/settings", {
        in_app_enabled: settings.in_app_enabled, email_enabled: settings.email_enabled,
        phone_call_reminders_enabled: settings.phone_call_reminders_enabled, sms_enabled: settings.sms_enabled,
        sms_compliance_approved: settings.sms_compliance_approved, sms_opt_out_instructions: settings.sms_opt_out_instructions,
        default_quiet_hours_start: settings.default_quiet_hours_start, default_quiet_hours_end: settings.default_quiet_hours_end,
        urgent_escalation_threshold_minutes: settings.urgent_escalation_threshold_minutes,
        high_escalation_threshold_minutes: settings.high_escalation_threshold_minutes,
        normal_escalation_threshold_minutes: settings.normal_escalation_threshold_minutes,
        sla_worker_enabled: settings.sla_worker_enabled, sla_phone_task_on_escalation: settings.sla_phone_task_on_escalation,
        sla_central_fallback_enabled: settings.sla_central_fallback_enabled,
        dedupe_window_minutes: settings.dedupe_window_minutes, marketing_unsubscribe_text: settings.marketing_unsubscribe_text,
        transactional_disclaimer: settings.transactional_disclaimer,
      });
      setSettings({ ...data, can_edit: settings.can_edit, sms_gate: data.sms_gate }); toast.success("Communication settings saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingS(false); }
  };

  if (!prefs) return <div className="max-w-2xl mx-auto space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-64" /></div>;

  return (
    <div className="max-w-2xl mx-auto pb-16" data-testid="comms-settings-page">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <MessageSquareText size={22} style={{ color: C.navy }} /> Communications
      </h1>
      <p className="text-[13px] mb-4" style={{ color: C.slate }}>How you're notified, and (for admins) the organization's notification & compliance settings.</p>

      {/* My preferences */}
      <Card className="p-5 border-[#E6EDF2] mb-5" data-testid="my-comm-prefs">
        <div className="text-[13px] font-semibold mb-1" style={{ color: C.navy }}>My notification preferences</div>
        <Row title={<span className="flex items-center gap-1.5"><Bell size={14} /> In-app notifications</span>} desc="Show alerts in the app.">
          <Switch data-testid="pref-in-app" checked={prefs.in_app_enabled} onCheckedChange={(v) => setP("in_app_enabled", v)} />
        </Row>
        <Row title={<span className="flex items-center gap-1.5"><Mail size={14} /> Email notifications</span>} desc="Receive email alerts.">
          <Switch data-testid="pref-email" checked={prefs.email_enabled} onCheckedChange={(v) => setP("email_enabled", v)} />
        </Row>
        <Row title={<span className="flex items-center gap-1.5"><Phone size={14} /> Phone-call task reminders</span>} desc="Create a call-back task when a call is needed.">
          <Switch data-testid="pref-phone" checked={prefs.phone_call_task_enabled} onCheckedChange={(v) => setP("phone_call_task_enabled", v)} />
        </Row>
        <Row title="Preferred contact method" desc="Used when routing chooses one channel.">
          <Select value={prefs.preferred_contact_method} onValueChange={(v) => setP("preferred_contact_method", v)}>
            <SelectTrigger className="h-8 w-[150px] text-[13px]" data-testid="pref-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="in_app">In-app</SelectItem>
              <SelectItem value="phone">Phone</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        <Row title="Quiet hours" desc="Non-urgent emails are held during these hours.">
          <div className="flex items-center gap-1.5">
            <Input type="time" data-testid="pref-quiet-start" value={prefs.quiet_hours_start || ""} onChange={(e) => setP("quiet_hours_start", e.target.value)} className="h-8 w-[110px] text-[13px]" />
            <span className="text-[12px]" style={{ color: C.slate }}>to</span>
            <Input type="time" data-testid="pref-quiet-end" value={prefs.quiet_hours_end || ""} onChange={(e) => setP("quiet_hours_end", e.target.value)} className="h-8 w-[110px] text-[13px]" />
          </div>
        </Row>
        <Row title="Marketing emails" desc="Optional product & tips emails (has an opt-out).">
          <Switch data-testid="pref-marketing" checked={prefs.marketing_email_opt_in} onCheckedChange={(v) => setP("marketing_email_opt_in", v)} />
        </Row>
        <div className="flex justify-end mt-3">
          <Button onClick={savePrefs} disabled={savingP} data-testid="save-prefs-btn" className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
            {savingP ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save my preferences
          </Button>
        </div>
      </Card>

      {/* My availability / backup (staff routing) */}
      {avail && (
        <Card className="p-5 border-[#E6EDF2] mb-5" data-testid="my-availability-card">
          <div className="text-[13px] font-semibold mb-1 flex items-center gap-1.5" style={{ color: C.navy }}><UserCheck size={14} /> My availability &amp; backup</div>
          <p className="text-[12px] mb-1" style={{ color: C.slate }}>Used by routing: when you're unavailable, new items go to your backup, then your team, then a central admin.</p>
          <Row title="Available now" desc="Turn off to route new items to your backup.">
            <Switch data-testid="avail-available-now" checked={avail.available_now !== false} onCheckedChange={(v) => setAv("available_now", v)} />
          </Row>
          <Row title="Out of office" desc="Marks you unavailable for all routing.">
            <Switch data-testid="avail-ooo" checked={!!avail.out_of_office} onCheckedChange={(v) => setAv("out_of_office", v)} />
          </Row>
          <Row title="Backup person" desc="Who covers your routed items while you're away.">
            <Select value={avail.backup_user_id || "none"} onValueChange={(v) => setAv("backup_user_id", v === "none" ? null : v)}>
              <SelectTrigger className="h-8 w-[190px] text-[13px]" data-testid="avail-backup"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No backup</SelectItem>
                {staff.filter((s) => s.id !== user?.id).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row title="Working hours" desc="Optional note (e.g. Mon–Fri 9–5).">
            <Input value={avail.working_hours || ""} onChange={(e) => setAv("working_hours", e.target.value)} placeholder="Mon–Fri 9–5" className="h-8 w-[190px] text-[13px]" data-testid="avail-hours" />
          </Row>
          <div className="flex justify-end mt-3">
            <Button onClick={saveAvail} disabled={savingA} data-testid="save-availability-btn" className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
              {savingA ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save availability
            </Button>
          </div>
        </Card>
      )}

      {/* SMS gate (visible to all staff — status only) */}
      <Card className="p-4 border-[#E6EDF2] mb-5" data-testid="sms-gate-card">
        <div className="flex items-start gap-2">
          <Smartphone size={16} style={{ color: C.amber }} className="mt-0.5" />
          <div>
            <div className="text-[13px] font-semibold" style={{ color: C.charcoal }}>SMS / Text messaging</div>
            <div className="text-[12.5px]" style={{ color: C.amber }}>SMS is disabled until provider setup, compliance approval, and opt-in are complete.</div>
            <div className="mt-1.5"><ProviderChip name="Twilio" status="not_connected" /></div>
          </div>
        </div>
      </Card>

      {/* Admin org settings */}
      {isAdmin && settings && (
        <Card className="p-5 border-[#E6EDF2]" data-testid="org-comm-settings">
          <div className="text-[13px] font-semibold mb-1" style={{ color: C.navy }}>Organization communication settings</div>
          {!settings.can_edit && <div className="rounded-lg p-2.5 mb-2 text-[12px]" style={{ background: "#EEF2F5", color: C.slate }}>Only a Super Admin can change these.</div>}
          <Row title="In-app notifications (org-wide)"><Switch data-testid="set-in-app" disabled={!settings.can_edit} checked={settings.in_app_enabled} onCheckedChange={(v) => setS("in_app_enabled", v)} /></Row>
          <Row title="Email notifications (org-wide)"><Switch data-testid="set-email" disabled={!settings.can_edit} checked={settings.email_enabled} onCheckedChange={(v) => setS("email_enabled", v)} /></Row>
          <Row title="Phone-call task reminders (org-wide)"><Switch data-testid="set-phone" disabled={!settings.can_edit} checked={settings.phone_call_reminders_enabled} onCheckedChange={(v) => setS("phone_call_reminders_enabled", v)} /></Row>
          <Row title="SMS enabled (org-wide)" desc="Stays blocked by the compliance gate below."><Switch data-testid="set-sms" disabled={!settings.can_edit} checked={settings.sms_enabled} onCheckedChange={(v) => setS("sms_enabled", v)} /></Row>
          <Row title="SMS compliance approved"><Switch data-testid="set-sms-compliance" disabled={!settings.can_edit} checked={settings.sms_compliance_approved} onCheckedChange={(v) => setS("sms_compliance_approved", v)} /></Row>

          <div className="py-3 border-b" style={{ borderColor: C.line }}>
            <div className="text-[12px] font-medium mb-1" style={{ color: C.charcoal }}>Provider status (placeholders)</div>
            <div className="flex flex-wrap gap-1.5">
              <ProviderChip name="email_outbox" status="connected" />
              <ProviderChip name="SendGrid" status={settings.providers?.email?.sendgrid} />
              <ProviderChip name="Resend" status={settings.providers?.email?.resend} />
              <ProviderChip name="Mailgun" status={settings.providers?.email?.mailgun} />
              <ProviderChip name="Twilio" status={settings.providers?.sms?.twilio} />
            </div>
          </div>

          {settings.sms_gate && !settings.sms_gate.allowed && (
            <div className="rounded-lg p-2.5 my-3 text-[12px]" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="sms-gate-reasons">
              <div className="font-semibold flex items-center gap-1"><ShieldAlert size={13} /> SMS blocked</div>
              <ul className="list-disc pl-5 mt-1">{settings.sms_gate.blocked_reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}

          <Row title="Default quiet hours">
            <div className="flex items-center gap-1.5">
              <Input type="time" disabled={!settings.can_edit} value={settings.default_quiet_hours_start} onChange={(e) => setS("default_quiet_hours_start", e.target.value)} className="h-8 w-[110px] text-[13px]" />
              <span className="text-[12px]" style={{ color: C.slate }}>to</span>
              <Input type="time" disabled={!settings.can_edit} value={settings.default_quiet_hours_end} onChange={(e) => setS("default_quiet_hours_end", e.target.value)} className="h-8 w-[110px] text-[13px]" />
            </div>
          </Row>
          <Row title="Urgent escalation threshold (min)" desc="Escalate if no response within this window.">
            <Input type="number" disabled={!settings.can_edit} value={settings.urgent_escalation_threshold_minutes} onChange={(e) => setS("urgent_escalation_threshold_minutes", e.target.value)} className="h-8 w-[90px] text-[13px]" data-testid="set-threshold" />
          </Row>
          <Row title="High-priority escalation threshold (min)" desc="Auto-escalate unanswered high-priority items.">
            <Input type="number" disabled={!settings.can_edit} value={settings.high_escalation_threshold_minutes ?? 120} onChange={(e) => setS("high_escalation_threshold_minutes", e.target.value)} className="h-8 w-[90px] text-[13px]" data-testid="set-high-threshold" />
          </Row>
          <Row title="Normal / document escalation threshold (min)" desc="Escalate stale document-review & normal items (default next business day).">
            <Input type="number" disabled={!settings.can_edit} value={settings.normal_escalation_threshold_minutes ?? 1440} onChange={(e) => setS("normal_escalation_threshold_minutes", e.target.value)} className="h-8 w-[90px] text-[13px]" data-testid="set-normal-threshold" />
          </Row>
          <Row title="SLA auto-escalation worker" desc="Automatically escalate items that sit unanswered past their threshold.">
            <Switch data-testid="set-sla-worker" disabled={!settings.can_edit} checked={settings.sla_worker_enabled !== false} onCheckedChange={(v) => setS("sla_worker_enabled", v)} />
          </Row>
          <Row title="Create phone-call task on escalation" desc="Adds a call-back task for the escalation recipient (never an automated call).">
            <Switch data-testid="set-sla-phone" disabled={!settings.can_edit} checked={!!settings.sla_phone_task_on_escalation} onCheckedChange={(v) => setS("sla_phone_task_on_escalation", v)} />
          </Row>
          <Row title="Central-admin fallback on escalation" desc="Notify a central admin when nobody else is available (escalation still logged if off).">
            <Switch data-testid="set-sla-central" disabled={!settings.can_edit} checked={settings.sla_central_fallback_enabled !== false} onCheckedChange={(v) => setS("sla_central_fallback_enabled", v)} />
          </Row>
          <Row title="Dedupe window (min)" desc="Suppress duplicate alerts within this window.">
            <Input type="number" disabled={!settings.can_edit} value={settings.dedupe_window_minutes} onChange={(e) => setS("dedupe_window_minutes", e.target.value)} className="h-8 w-[90px] text-[13px]" data-testid="set-dedupe" />
          </Row>
          <Row title="Central admin fallback" desc="Empty = auto-route unassigned urgent items to Super Admins.">
            <span className="text-[12px]" style={{ color: C.slate }} data-testid="set-fallback">{(settings.central_admin_fallback_user_ids?.length) ? `${settings.central_admin_fallback_user_ids.length} configured` : "Auto: Super Admins"}</span>
          </Row>
          <div className="pt-3 space-y-3">
            <div>
              <Label className="text-[12px]" style={{ color: C.slate }}>SMS opt-out instructions</Label>
              <Input disabled={!settings.can_edit} value={settings.sms_opt_out_instructions} onChange={(e) => setS("sms_opt_out_instructions", e.target.value)} placeholder="e.g. Reply STOP to opt out" className="mt-1 h-8 text-[13px]" />
            </div>
            <div>
              <Label className="text-[12px]" style={{ color: C.slate }}>Marketing unsubscribe text</Label>
              <Textarea disabled={!settings.can_edit} value={settings.marketing_unsubscribe_text} onChange={(e) => setS("marketing_unsubscribe_text", e.target.value)} rows={2} className="mt-1 text-[13px]" />
            </div>
            <div>
              <Label className="text-[12px]" style={{ color: C.slate }}>Transactional disclaimer</Label>
              <Textarea disabled={!settings.can_edit} value={settings.transactional_disclaimer} onChange={(e) => setS("transactional_disclaimer", e.target.value)} rows={2} className="mt-1 text-[13px]" />
            </div>
          </div>
          {settings.can_edit && (
            <div className="flex justify-end mt-4">
              <Button onClick={saveSettings} disabled={savingS} data-testid="save-settings-btn" className="h-9 gap-1 bg-[#1C4358] hover:bg-[#16374a] text-white">
                {savingS ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save settings
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
